import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  useTheme,
  Avatar,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tabs,
  Tab,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  Drawer,
  Badge,
  TableSortLabel,
  InputAdornment,
  Button,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as CartIcon,
  FamilyRestroom as FamilyIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon,
  LocalAtm as LocalAtmIcon,
  Groups as GroupsIcon,
  HomeWork as HomeWorkIcon,
} from '@mui/icons-material';
import api from '../config/api.js';
import { formatCurrency } from '../utils/formatters';
import DatePickerField from '../components/common/DatePickerField';
import MobileDashboard from '../components/Dashboard/MobileDashboard';
import NewsSection from '../components/Dashboard/NewsSection';

// Format dates for display (MM/DD/YYYY)
const formatDateForDisplay = (dateStr, dateType) => {
  if (!dateStr) return '';
  
  // If the string already has slashes (m/d/yyyy format), return as is
  if (dateStr.includes('/')) return dateStr;
  
  // Handle "IP" case
  if (dateStr === "IP") return "IP";
  
  // Parse YYYY-MM-DD HH:mm:ss format
  const [datePart] = dateStr.split(' ');
  const [year, month, day] = datePart.split('-');
  return `${month}/${day}/${year}`;
};

const getDateRangeTitle = (startDate, endDate, dateRange, t) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Parse YYYY-MM-DD safely to avoid timezone issues
  const parseYMD = (str) => {
    if (!str) return null;
    const [year, month, day] = str.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const start = parseYMD(startDate);
  const end = parseYMD(endDate);

  // Format date as "MMM d, yyyy" (e.g., "Jan 1, 2024")
  const formatDate = (date) => {
    return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  };

  // Format month as "MMM yyyy" (e.g., "Jan 2024")
  const formatMonth = (date) => {
    return date ? date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
  };

  // Helper function to compare dates ignoring time
  const isSameDay = (date1, date2) => {
    return date1 && date2 &&
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  };

  switch (dateRange) {
    case 'day':
      if (isSameDay(end, today)) {
        return t('common.today');
      } else if (isSameDay(end, yesterday)) {
        return t('common.yesterday');
      } else {
        return formatDate(end);
      }
    case 'week': {
      const thisWeekStart = new Date(today);
      thisWeekStart.setDate(today.getDate() - today.getDay());
      const thisWeekEnd = new Date(thisWeekStart);
      thisWeekEnd.setDate(thisWeekStart.getDate() + 6);

      const lastWeekStart = new Date(thisWeekStart);
      lastWeekStart.setDate(thisWeekStart.getDate() - 7);
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6);

      if (isSameDay(end, thisWeekEnd)) {
        return t('common.thisWeek');
      } else if (isSameDay(end, lastWeekEnd)) {
        return t('common.lastWeek');
      } else {
        return `${formatDate(start)} - ${formatDate(end)}`;
      }
    }
    case 'month': {
      const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

      if (end && end.getFullYear() === thisMonthEnd.getFullYear() && end.getMonth() === thisMonthEnd.getMonth()) {
        return t('common.thisMonth');
      } else if (end && end.getFullYear() === lastMonthEnd.getFullYear() && end.getMonth() === lastMonthEnd.getMonth()) {
        return t('common.lastMonth');
      } else {
        return formatMonth(end);
      }
    }
    case 'ytd':
      return `YTD ${start ? start.getFullYear() : ''}`;
    default:
      return `${formatDate(start)} - ${formatDate(end)}`;
  }
};

const StatCard = ({ title, value, icon, color }) => {
  return (
    <Paper
      sx={{
        flex: 1,
        minWidth: 0,
        p: { xs: 1, sm: 1.5 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box
          sx={{
            p: 0.8,
            borderRadius: '10px',
            bgcolor: 'rgba(44, 156, 108, 0.08)',
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': { fontSize: '1rem' },
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'text.secondary',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: '#F0F3F2',
          borderRadius: '8px',
          p: 1,
        }}
      >
        <Typography
          component="p"
          sx={{
            fontSize: { xs: '1.3rem', sm: '1.5rem' },
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Paper>
  );
};

const WriterTableRow = ({ writer, closes }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  // Ensure closes is an array with fallback to empty array
  const closesArray = Array.isArray(closes) ? closes : [];

  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(44, 156, 108, 0.08)',
          },
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {writer.name}
        </TableCell>
        <TableCell align="right">{formatCurrency(writer.totalAP)}</TableCell>
        <TableCell align="right">{writer.policies}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t('dashboard.closesBreakdown')}
              </Typography>
              <Table size="small" aria-label="closes">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('dashboard.draft')}</TableCell>
                    <TableCell>{t('common.submitDate')}</TableCell>
                    <TableCell>{t('common.carrier')}</TableCell>
                    <TableCell>{t('common.product')}</TableCell>
                    <TableCell>{t('common.leadType')}</TableCell>
                    <TableCell align="right">{t('common.ap')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {closesArray.map((close, index) => (
                    <TableRow key={index}>
                      <TableCell>{close.draft}</TableCell>
                      <TableCell>{formatDateForDisplay(close.submitDate, 'submit')}</TableCell>
                      <TableCell>{close.carrier}</TableCell>
                      <TableCell>{close.product}</TableCell>
                      <TableCell>{close.lead_type}</TableCell>
                      <TableCell align="right">{formatCurrency(close.ap)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {closesArray.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.noClosesData')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Top 3 display with circular avatars - replaces the old podium
const TopThreeDisplay = ({ writers, profilePictures = {} }) => {
  const top3 = writers.slice(0, 3);
  // Display order: 2nd left, 1st center, 3rd right
  const displayOrder = [1, 0, 2];
  const sizes = { 0: 100, 1: 76, 2: 76 };
  const borderColors = {
    0: '#2C9C6C', // 1st - teal
    1: '#2C9C6C', // 2nd - teal
    2: '#8A9B93', // 3rd - muted
  };
  const markerColors = {
    0: '#2C9C6C',
    1: '#2C9C6C',
    2: '#8A9B93',
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end',
      gap: { xs: 3, sm: 5 },
      py: 2.5,
      px: 2,
    }}>
      {displayOrder.map((origIdx) => {
        const writer = top3[origIdx];
        if (!writer) return null;
        const size = sizes[origIdx];
        const borderColor = borderColors[origIdx];
        const markerColor = markerColors[origIdx];
        const pic = profilePictures[writer.name];

        return (
          <Box
            key={writer.id || writer.name}
            sx={{
              flex: 1,
              width: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: origIdx === 0 ? 2 : 0,
            }}
          >
            {/* Rank number */}
            <Typography sx={{
              fontSize: origIdx === 0 ? '1.1rem' : '0.95rem',
              fontWeight: 700,
              color: markerColor,
              mb: 0.5,
            }}>
              {origIdx + 1}
            </Typography>

            {/* Crown for 1st, triangle markers for 2nd/3rd */}
            {origIdx === 0 ? (
              <Typography sx={{ fontSize: '1.5rem', mb: 0.5, lineHeight: 1 }}>
                👑
              </Typography>
            ) : (
              <Typography sx={{
                fontSize: '0.8rem',
                color: markerColor,
                mb: 0.5,
                lineHeight: 1,
              }}>
                ▲
              </Typography>
            )}

            {/* Avatar with ring */}
            <Box sx={{ position: 'relative', mb: 1.5 }}>
              <Avatar
                src={pic || undefined}
                sx={{
                  width: size,
                  height: size,
                  border: `3px solid ${borderColor}`,
                  bgcolor: '#E8EDEB',
                  color: borderColor,
                }}
              >
                {!pic && <PersonIcon sx={{ fontSize: size * 0.5 }} />}
              </Avatar>
            </Box>

            {/* Name */}
            <Typography sx={{
              fontWeight: 600,
              fontSize: origIdx === 0 ? '0.95rem' : '0.85rem',
              color: 'text.primary',
              textAlign: 'center',
              width: '100%',
              lineHeight: 1.2,
            }}>
              {writer.name}
            </Typography>

            {/* Score */}
            <Typography sx={{
              fontWeight: 700,
              fontSize: origIdx === 0 ? '1.3rem' : '1.1rem',
              color: '#2C9C6C',
              mt: 0.5,
            }}>
              {formatCurrency(writer.total_premium)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [isAdmin, setIsAdmin] = useState(false);
  const [profilePictures, setProfilePictures] = useState({});
  
  // Get first and last day of current month (use UTC to avoid timezone boundary issues)
  const today = new Date();
  const firstDayOfMonth = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1));
  const lastDayOfMonth = new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1, 0));
  
  // Format dates for input fields
  const formatDateForInput = (date) => {
    if (!date) return '';
    
    // Handle both Date objects and UTC Date objects
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    
    // Return in YYYY-MM-DD format for the date picker
    return `${year}-${month}-${day}`;
  };

  // Parse date string based on format
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    // Parse from YYYY-MM-DD format
    return new Date(dateStr);
  };

  const [stats, setStats] = useState(null);
  const [topAgents, setTopAgents] = useState([]);
  const [writerDetails, setWriterDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allCloses, setAllCloses] = useState([]);
  const [filters, setFilters] = useState({
    carrier: [],
    product: [],
    writer_id: [],
    agency: [],
    dateType: 'submit',
    startDate: formatDateForInput(firstDayOfMonth),
    endDate: formatDateForInput(lastDayOfMonth),
  });
  const [filterOptions, setFilterOptions] = useState({
    carriers: [],
    products: [],
    agencies: [],
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('totalAP');
  const [order, setOrder] = useState('desc');
  const [dateRange, setDateRange] = useState('month');
  const [viewMode, setViewMode] = useState('agent'); // 'agent' or 'agency'
  
  // Leaderboard-specific state variables
  const [isLoading, setIsLoading] = useState(false);
  const [writers, setWriters] = useState([]);
  const [topWriters, setTopWriters] = useState([]);
  const [displayedWriters, setDisplayedWriters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [leaderboardExpanded, setLeaderboardExpanded] = useState(false);

  // Admin check
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.is_admin) setIsAdmin(true);
    api.get('/auth/me')
      .then(res => setIsAdmin(res.data.is_admin === 1))
      .catch(() => {});
  }, []);

  // Current user rank
  const currentUserRank = useMemo(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user.name;
    if (!userName || topAgents.length === 0) return null;
    const index = topAgents.findIndex(
      a => a.name.toLowerCase().trim() === userName.toLowerCase().trim()
    );
    return index >= 0 ? index + 1 : null;
  }, [topAgents]);

  // Fetch profile pictures for leaderboard
  useEffect(() => {
    if (topAgents.length > 0) {
      const names = topAgents.slice(0, 10).map(a => a.name).join(',');
      api.get(`/auth/profile-pictures?names=${encodeURIComponent(names)}`)
        .then(res => setProfilePictures(res.data))
        .catch(() => {});
    }
  }, [topAgents]);

  // Calculate if current period is today/this week/this month
  const isCurrentPeriod = useMemo(() => {
    const currentStartDate = new Date(filters.startDate);
    const currentEndDate = new Date(filters.endDate);
    
    switch (dateRange) {
      case 'day': {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return currentEndDate.getTime() === today.getTime();
      }
      case 'week': {
        const today = new Date();
        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - today.getDay());
        thisWeekStart.setHours(0, 0, 0, 0);
        const thisWeekEnd = new Date(thisWeekStart);
        thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
        thisWeekEnd.setHours(23, 59, 59, 999);
        
        return currentStartDate.getTime() === thisWeekStart.getTime() && 
               currentEndDate.getTime() === thisWeekEnd.getTime();
      }
      case 'month': {
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        thisMonthEnd.setHours(23, 59, 59, 999);
        
        return currentStartDate.getTime() === thisMonthStart.getTime() && 
               currentEndDate.getTime() === thisMonthEnd.getTime();
      }
      default:
        return false;
    }
  }, [filters.startDate, filters.endDate, dateRange]);

  // Update active filters count whenever filters change
  useEffect(() => {
    const count = [
      filters.carrier.length > 0, 
      filters.product.length > 0,
      filters.writer_id.length > 0,
      filters.agency.length > 0
    ].filter(Boolean).length;
    setActiveFilters(count);
  }, [filters.carrier, filters.product, filters.writer_id, filters.agency]);
  
  // Effect to prepare leaderboard data from topAgents
  useEffect(() => {
    if (topAgents.length > 0) {
      // Prepare writer data with proper structure
      const formattedWriters = topAgents.map(item => ({
        id: item.name,
        name: item.name,
        totalAP: item.totalAP,
        policies: item.closes || 0, // Number of policies
        total_premium: item.totalAP,
        viewMode: viewMode,
        // Make sure closes is an array of individual close records
        closes: writerDetails[item.name] || []
      }));
      
      setTopWriters(formattedWriters);
      setDisplayedWriters(
        formattedWriters.filter(writer => 
          writer.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      
      // Extract all items for select dropdown (agents or agencies based on view mode)
      setWriters(formattedWriters.map(writer => ({
        id: writer.id,
        name: writer.name
      })));
    }
  }, [topAgents, searchTerm, writerDetails, viewMode]);

  // Effect to update displayed writers when sort/filter criteria change
  useEffect(() => {
    if (topWriters.length > 0) {
      setDisplayedWriters(filterAndSortWriters());
    }
  }, [orderBy, order, searchTerm, topWriters, viewMode]);

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/Closes');
        const closesData = response.data;

        if (!Array.isArray(closesData)) {
          throw new Error('Invalid data format received from server');
        }

        // Extract unique values for filters
        const carriers = [...new Set(closesData.map(close => close.carrier))].filter(Boolean);
        const products = [...new Set(closesData.map(close => close.product))].filter(Boolean);
        const agencies = [...new Set(closesData.map(close => close.agency))]
          .filter(agency => agency && agency.toUpperCase() !== 'EIP');

        setFilterOptions({
          carriers,
          products,
          agencies,
        });

        setAllCloses(closesData);
        
        // Initialize filters with team from localStorage if available
        const savedTeam = localStorage.getItem('selectedTeam');
        const initialFilters = {
          ...filters,
          team: savedTeam || 'All Teams'
        };
        setFilters(initialFilters);
        updateFilteredData(closesData, initialFilters);
        
        // Ensure team change is called with initial value
        if (savedTeam) {
          localStorage.setItem('selectedTeam', savedTeam);
        } else {
          localStorage.removeItem('selectedTeam');
        }
      } catch (err) {

        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const updateFilteredData = (closes, currentFilters, currentViewMode = viewMode) => {
    // Filter closes based on current filters
    const filteredCloses = closes.filter(close => {
      // Check if carrier matches any of the selected carriers or if no carriers are selected
      const matchesCarrier = currentFilters.carrier.length === 0 || 
                            currentFilters.carrier.includes(close.carrier);
      
      // Check if product matches any of the selected products or if no products are selected
      const matchesProduct = currentFilters.product.length === 0 || 
                            currentFilters.product.includes(close.product);
      
      // Check if writer matches any of the selected writers or if no writers are selected
      const matchesWriter = currentFilters.writer_id.length === 0 || 
                           currentFilters.writer_id.includes(close.agent);
      
      // Check if agency matches any of the selected agencies or if no agencies are selected
      const matchesAgency = currentFilters.agency.length === 0 || 
                            currentFilters.agency.includes(close.agency);
      
      // Date filtering
      let matchesDate = true;
      if (currentFilters.dateType && (currentFilters.startDate || currentFilters.endDate)) {
        // If filtering by effective date, use the draft field which may contain a date
        if (currentFilters.dateType === 'effective') {
          // If draft is "IP", include it if the submit date is within range
          if (close.draft === 'IP') {
            const submitDateStr = close.submitDate;
            if (!submitDateStr) {
              matchesDate = false;
            } else {
              // Parse submit date
              const [datePart] = submitDateStr.split(' ');
              const [year, month, day] = datePart.split('-');
              // Create date at UTC midnight to avoid timezone issues
              const submitDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
              
              // Check against filter dates
              if (currentFilters.startDate) {
                const [sYear, sMonth, sDay] = currentFilters.startDate.split('-');
                const startDate = new Date(Date.UTC(sYear, sMonth - 1, sDay, 0, 0, 0));
                const isAfterStart = submitDate >= startDate;
                matchesDate = matchesDate && isAfterStart;
              }

              if (currentFilters.endDate) {
                const [eYear, eMonth, eDay] = currentFilters.endDate.split('-');
                const endDate = new Date(Date.UTC(eYear, eMonth - 1, eDay, 23, 59, 59, 999));
                const isBeforeEnd = submitDate <= endDate;
                matchesDate = matchesDate && isBeforeEnd;
              }
            }
          }
          // If draft contains a date (m/d/yyyy format), use it for filtering
          else if (close.draft && close.draft.includes('/')) {
            try {
              // Parse m/d/yyyy format
              const [month, day, year] = close.draft.split('/');
              // Create date at UTC midnight to avoid timezone issues
              const draftDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
              
              // Check against filter dates
              if (currentFilters.startDate) {
                const [sYear, sMonth, sDay] = currentFilters.startDate.split('-');
                const startDate = new Date(Date.UTC(sYear, sMonth - 1, sDay, 0, 0, 0));
                const isAfterStart = draftDate >= startDate;
                matchesDate = matchesDate && isAfterStart;
              }
              
              if (currentFilters.endDate) {
                const [eYear, eMonth, eDay] = currentFilters.endDate.split('-');
                const endDate = new Date(Date.UTC(eYear, eMonth - 1, eDay, 23, 59, 59, 999));
                const isBeforeEnd = draftDate <= endDate;
                matchesDate = matchesDate && isBeforeEnd;
              }
            } catch (e) {
              // If date parsing fails, exclude the record
              matchesDate = false;
            }
          } else {
            // For other draft values, use submit date as fallback
            const submitDateStr = close.submitDate;
            if (!submitDateStr) {
              matchesDate = false;
            } else {
              // Parse submit date
              const [datePart] = submitDateStr.split(' ');
              const [year, month, day] = datePart.split('-');
              // Create date at UTC midnight to avoid timezone issues
              const submitDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
              
              // Check against filter dates
              if (currentFilters.startDate) {
                const [sYear, sMonth, sDay] = currentFilters.startDate.split('-');
                const startDate = new Date(Date.UTC(sYear, sMonth - 1, sDay, 0, 0, 0));
                const isAfterStart = submitDate >= startDate;
                matchesDate = matchesDate && isAfterStart;
              }

              if (currentFilters.endDate) {
                const [eYear, eMonth, eDay] = currentFilters.endDate.split('-');
                const endDate = new Date(Date.UTC(eYear, eMonth - 1, eDay, 23, 59, 59, 999));
                const isBeforeEnd = submitDate <= endDate;
                matchesDate = matchesDate && isBeforeEnd;
              }
            }
          }
        } 
        // Regular submit date filtering
        else {
          const dateField = 'submitDate';
          const dateStr = close[dateField];
          
          if (!dateStr) {
            matchesDate = false;
          } else {
            // Parse YYYY-MM-DD HH:mm:ss format
            const [datePart, timePart] = dateStr.split(' ');
            const [year, month, day] = datePart.split('-');
            // Create date at UTC midnight to avoid timezone issues
            const closeDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
            
            if (currentFilters.startDate) {
              const [sYear, sMonth, sDay] = currentFilters.startDate.split('-');
              const startDate = new Date(Date.UTC(sYear, sMonth - 1, sDay, 0, 0, 0));
              const isAfterStart = closeDate >= startDate;
              matchesDate = matchesDate && isAfterStart;
            }

            if (currentFilters.endDate) {
              const [eYear, eMonth, eDay] = currentFilters.endDate.split('-');
              const endDate = new Date(Date.UTC(eYear, eMonth - 1, eDay, 23, 59, 59, 999));
              const isBeforeEnd = closeDate <= endDate;
              matchesDate = matchesDate && isBeforeEnd;
            }
          }
        }
      }

      return matchesCarrier && matchesProduct && matchesWriter && matchesAgency && matchesDate;
    });

    // Calculate stats
    const totalAP = filteredCloses.reduce((sum, record) => sum + (Number(record.ap) || 0), 0);
    const uniqueAgents = new Set(filteredCloses.map(record => record.agent)).size;
    const uniqueAgencies = new Set(
      filteredCloses
        .map(record => record.agency)
        .filter(agency => agency && agency.toUpperCase() !== 'EIP')
    ).size;

    setStats({
      totalAP,
      uniqueAgents,
      uniqueAgencies,
      totalCloses: filteredCloses.length
    });

    // Calculate top agents
    const agentStats = filteredCloses.reduce((acc, record) => {
      const key = currentViewMode === 'agency' ? record.agency : record.agent;
      const keyLabel = currentViewMode === 'agency' ? 'agency' : 'agent';
      
      if (!key) {
        return acc; // Skip records without the required field
      }
      
      // Filter out "EIP" when in agency view mode
      if (currentViewMode === 'agency' && key.toUpperCase() === 'EIP') {
        return acc;
      }
      
      if (!acc[key]) {
        acc[key] = {
          name: key,
          totalAP: 0,
          closes: 0,
          latestSubmitDate: record.submitDate,
          viewMode: currentViewMode,
          [keyLabel]: key
        };
      }
      acc[key].totalAP += Number(record.ap) || 0;
      acc[key].closes += 1;
      
      if (record.submitDate && (!acc[key].latestSubmitDate || record.submitDate > acc[key].latestSubmitDate)) {
        acc[key].latestSubmitDate = record.submitDate;
      }
      
      return acc;
    }, {});

    const topAgentsData = Object.values(agentStats)
      .sort((a, b) => b.totalAP - a.totalAP);

    setTopAgents(topAgentsData);

    // Group closes by the selected view mode (agent or agency)
    const closesByKey = filteredCloses.reduce((acc, close) => {
      const key = currentViewMode === 'agency' ? close.agency : close.agent;
      if (!key) return acc; // Skip records without the required field
      
      // Filter out "EIP" when in agency view mode
      if (currentViewMode === 'agency' && key.toUpperCase() === 'EIP') {
        return acc;
      }
      
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(close);
      return acc;
    }, {});

    setWriterDetails(closesByKey);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    let newValue = value;
    
    // Normalize team value to uppercase when setting the filter
    if (name === 'team') {
      newValue = value === 'All Teams' ? 'All Teams' : (value ? value.toUpperCase() : 'All Teams');
    }
    
    const newFilters = {
      ...filters,
      [name]: newValue
    };
    
    setFilters(newFilters);
    updateFilteredData(allCloses, newFilters);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      // Re-process the data with the new view mode immediately
      updateFilteredData(allCloses, filters, newViewMode);
    }
  };

  const handleTabChange = (event, newValue) => {
    const newFilters = {
      ...filters,
      dateType: newValue
    };
    setFilters(newFilters);
    updateFilteredData(allCloses, newFilters);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      const isAsc = order === 'asc';
      let comparison = 0;

      switch (orderBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'totalAP':
          comparison = a.totalAP - b.totalAP;
          break;
        case 'policies':
          comparison = a.policies - b.policies;
          break;
        default:
          comparison = 0;
      }

      return isAsc ? comparison : -comparison;
    });
  };

  const filterAndSortWriters = () => {
    const filteredWriters = topWriters.filter(writer =>
      writer.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return sortData(filteredWriters);
  };

  // Add clear filters handler
  const handleClearFilters = async () => {
    try {
      setLoading(true);
      const response = await api.get('/Closes');
      const closesData = response.data;

      if (!Array.isArray(closesData)) {
        throw new Error('Invalid data format received from server');
      }
      
      // Extract unique values for filters
      const carriers = [...new Set(closesData.map(close => close.carrier))].filter(Boolean);
      const products = [...new Set(closesData.map(close => close.product))].filter(Boolean);
      const agencies = [...new Set(closesData.map(close => close.agency))]
        .filter(agency => agency && agency.toUpperCase() !== 'EIP');

      setFilterOptions({
        carriers,
        products,
        agencies,
      });

      setAllCloses(closesData);

      const newFilters = {
        carrier: [],
        product: [],
        writer_id: [],
        agency: [],
        dateType: filters.dateType,
        startDate: formatDateForInput(firstDayOfMonth),
        endDate: formatDateForInput(lastDayOfMonth),
      };
      setFilters(newFilters);
      updateFilteredData(closesData, newFilters);
      
      handleFilterClose();
    } catch (err) {

      setError(err.message || t('dashboard.failedToClearFilters'));
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (range) => {
    const today = new Date();
    let startDate, endDate;

    switch (range) {
      case 'day':
        startDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0));
        endDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999));
        break;
      case 'week':
        startDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay(), 0, 0, 0));
        endDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() + 6, 23, 59, 59, 999));
        break;
      case 'month':
        startDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1, 0, 0, 0));
        endDate = new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999));
        break;
      case 'ytd':
        startDate = new Date(Date.UTC(today.getFullYear(), 0, 1, 0, 0, 0));
        endDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999));
        break;
      default:
        return;
    }

    const newFilters = {
      ...filters,
      startDate: formatDateForInput(startDate),
      endDate: formatDateForInput(endDate)
    };
    setFilters(newFilters);
    setDateRange(range);
    updateFilteredData(allCloses, newFilters);
  };

  const handleDateShift = (direction) => {
    if (dateRange === 'ytd') return;
    // Parse dates as UTC to avoid timezone issues with date arithmetic
    const [sYear, sMonth, sDay] = filters.startDate.split('-').map(Number);
    const [eYear, eMonth] = filters.endDate.split('-').map(Number);
    const shiftAmount = direction === 'forward' ? 1 : -1;

    let newStartDate, newEndDate;

    switch (dateRange) {
      case 'day':
        newStartDate = new Date(Date.UTC(sYear, sMonth - 1, sDay + shiftAmount));
        newEndDate = new Date(newStartDate);
        break;
      case 'week':
        newStartDate = new Date(Date.UTC(sYear, sMonth - 1, sDay + (shiftAmount * 7)));
        newEndDate = new Date(Date.UTC(
          newStartDate.getUTCFullYear(),
          newStartDate.getUTCMonth(),
          newStartDate.getUTCDate() + 6
        ));
        break;
      case 'month':
        // Calculate the new month and year based on end date
        let newMonth = (eMonth - 1) + shiftAmount;
        let newYear = eYear;

        // Handle month overflow/underflow
        if (newMonth > 11) {
          newMonth = 0;
          newYear++;
        } else if (newMonth < 0) {
          newMonth = 11;
          newYear--;
        }

        // Create new dates with UTC to avoid timezone issues
        newStartDate = new Date(Date.UTC(newYear, newMonth, 1));
        newEndDate = new Date(Date.UTC(newYear, newMonth + 1, 0));

        break;
      default:
        return;
    }

    const formattedStartDate = formatDateForInput(newStartDate);
    const formattedEndDate = formatDateForInput(newEndDate);

    const newFilters = {
      ...filters,
      startDate: formattedStartDate,
      endDate: formattedEndDate
    };
    setFilters(newFilters);
    updateFilteredData(allCloses, newFilters);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Mobile non-admin view
  if (isMobile) {
    const mobilePeriodLabel = getDateRangeTitle(filters.startDate, filters.endDate, dateRange, t);
    return (
      <>
        <MobileDashboard
          stats={stats}
          viewMode={viewMode}
          topAgents={topAgents}
          currentUserRank={currentUserRank}
          profilePictures={profilePictures}
          dateRange={dateRange}
          periodLabel={mobilePeriodLabel}
          filters={filters}
          isAdmin={isAdmin}
          onDateRangeChange={handleDateRangeChange}
          onDateShift={handleDateShift}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          activeFilters={activeFilters}
          onFilterClick={() => setMobileFilterOpen(true)}
          t={t}
        />
        <Drawer
          anchor="bottom"
          open={mobileFilterOpen}
          onClose={() => setMobileFilterOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '80vh',
              p: 2.5,
            },
          }}
        >
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('dashboard.viewMode')}
            </Typography>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              fullWidth
              sx={{
                mb: 2,
                '& .MuiToggleButton-root': {
                  flex: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="agent">{t('dashboard.agentView')}</ToggleButton>
              <ToggleButton value="agency">{t('dashboard.agencyView')}</ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" fullWidth>
              <InputLabel>{t('common.carrier')}</InputLabel>
              <Select
                name="carrier"
                value={filters.carrier}
                label={t('common.carrier')}
                onChange={handleFilterChange}
                multiple
                renderValue={(selected) =>
                  selected.length === 0
                    ? t('common.all')
                    : selected.join(', ')
                }
              >
                {filterOptions.carriers.map(carrier => (
                  <MenuItem key={carrier} value={carrier}>{carrier}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>{t('common.product')}</InputLabel>
              <Select
                name="product"
                value={filters.product}
                label={t('common.product')}
                onChange={handleFilterChange}
                multiple
                renderValue={(selected) =>
                  selected.length === 0
                    ? t('common.all')
                    : selected.join(', ')
                }
              >
                {filterOptions.products.map(product => (
                  <MenuItem key={product} value={product}>{product}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>{t('common.agency')}</InputLabel>
              <Select
                name="agency"
                value={filters.agency}
                label={t('common.agency')}
                onChange={handleFilterChange}
                multiple
                renderValue={(selected) =>
                  selected.length === 0
                    ? t('common.allAgencies')
                    : selected.length === 1
                      ? selected[0]
                      : t('common.agenciesSelected', { count: selected.length })
                }
              >
                {filterOptions.agencies.map(agency => (
                  <MenuItem key={agency} value={agency}>{agency}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>{t('common.agent')}</InputLabel>
              <Select
                name="writer_id"
                value={filters.writer_id}
                onChange={handleFilterChange}
                label={t('common.agent')}
                multiple
                renderValue={(selected) =>
                  selected.length === 0
                    ? t('common.allAgents')
                    : selected.length === 1
                      ? writers.find(w => w.id === selected[0])?.name || selected[0]
                      : t('common.agentsSelected', { count: selected.length })
                }
              >
                {writers.map((writer) => (
                  <MenuItem key={writer.id} value={writer.id}>{writer.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButtonGroup
              value={filters.dateType}
              exclusive
              onChange={handleTabChange}
              size="small"
              fullWidth
              sx={{
                mb: 1,
                '& .MuiToggleButton-root': {
                  flex: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="submit">{t('common.submitDate')}</ToggleButton>
              <ToggleButton value="effective">{t('common.effectiveDate')}</ToggleButton>
            </ToggleButtonGroup>

            <DatePickerField
              label={t('common.startDate')}
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />

            <DatePickerField
              label={t('common.endDate')}
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />

            <Button
              variant="outlined"
              onClick={() => { handleClearFilters(); setMobileFilterOpen(false); }}
              fullWidth
              sx={{
                mt: 1,
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                },
              }}
            >
              {t('common.clearFilters')}
            </Button>
          </Stack>
        </Drawer>
      </>
    );
  }

  const periodLabel = getDateRangeTitle(filters.startDate, filters.endDate, dateRange, t);

  return (
    <Box sx={{
      flexGrow: 1,
      p: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '1400px',
      mx: 'auto',
    }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 1.5, sm: 2 },
              bgcolor: 'transparent',
              borderRadius: 3,
              border: '1px solid transparent',
              boxShadow: 'none',
            }}
          >

            {/* Period Label */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              {periodLabel}
            </Typography>

            {/* Date Range Controls */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder={viewMode === 'agency' ? t('dashboard.searchAgencies') : t('dashboard.searchWriters')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 200 }}
                />
                <IconButton
                  onClick={handleFilterClick}
                  sx={{
                    border: '1px solid transparent',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Badge badgeContent={activeFilters} color="primary">
                    <FilterListIcon />
                  </Badge>
                </IconButton>
              </Box>
            </Box>
            
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={handleFilterClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  p: 2,
                },
              }}
            >
              <Stack spacing={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('dashboard.viewMode')}
                </Typography>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  size="small"
                  fullWidth
                  sx={{
                    mb: 2,
                    '& .MuiToggleButton-root': {
                      flex: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="agent">{t('dashboard.agentView')}</ToggleButton>
                  <ToggleButton value="agency">{t('dashboard.agencyView')}</ToggleButton>
                </ToggleButtonGroup>

                <FormControl size="small" fullWidth>
                  <InputLabel>{t('common.carrier')}</InputLabel>
                  <Select
                    name="carrier"
                    value={filters.carrier}
                    label={t('common.carrier')}
                    onChange={handleFilterChange}
                    multiple
                    renderValue={(selected) =>
                      selected.length === 0
                        ? t('common.all')
                        : selected.join(', ')
                    }
                  >
                    {filterOptions.carriers.map(carrier => (
                      <MenuItem key={carrier} value={carrier}>{carrier}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>{t('common.product')}</InputLabel>
                  <Select
                    name="product"
                    value={filters.product}
                    label={t('common.product')}
                    onChange={handleFilterChange}
                    multiple
                    renderValue={(selected) =>
                      selected.length === 0
                        ? t('common.all')
                        : selected.join(', ')
                    }
                  >
                    {filterOptions.products.map(product => (
                      <MenuItem key={product} value={product}>{product}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>{t('common.agency')}</InputLabel>
                  <Select
                    name="agency"
                    value={filters.agency}
                    label={t('common.agency')}
                    onChange={handleFilterChange}
                    multiple
                    renderValue={(selected) =>
                      selected.length === 0
                        ? t('common.allAgencies')
                        : selected.length === 1
                          ? selected[0]
                          : t('common.agenciesSelected', { count: selected.length })
                    }
                  >
                    {filterOptions.agencies.map(agency => (
                      <MenuItem key={agency} value={agency}>{agency}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>{t('common.agent')}</InputLabel>
                  <Select
                    name="writer_id"
                    value={filters.writer_id}
                    onChange={handleFilterChange}
                    label={t('common.agent')}
                    multiple
                    renderValue={(selected) =>
                      selected.length === 0
                        ? t('common.allAgents')
                        : selected.length === 1
                          ? writers.find(w => w.id === selected[0])?.name || selected[0]
                          : t('common.agentsSelected', { count: selected.length })
                    }
                  >
                    {writers.map((writer) => (
                      <MenuItem key={writer.id} value={writer.id}>{writer.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <ToggleButtonGroup
                  value={filters.dateType}
                  exclusive
                  onChange={handleTabChange}
                  size="small"
                  fullWidth
                  sx={{
                    mb: 1,
                    '& .MuiToggleButton-root': {
                      flex: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="submit">{t('common.submitDate')}</ToggleButton>
                  <ToggleButton value="effective">{t('common.effectiveDate')}</ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleDateShift('back')}
                    disabled={dateRange === 'ytd'}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>

                  <ToggleButtonGroup
                    value={dateRange}
                    exclusive
                    onChange={(e, value) => value && handleDateRangeChange(value)}
                    size="small"
                    sx={{
                      flex: 1,
                      '& .MuiToggleButton-root': {
                        flex: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="day">{t('common.day')}</ToggleButton>
                    <ToggleButton value="week">{t('common.week')}</ToggleButton>
                    <ToggleButton value="month">{t('common.month')}</ToggleButton>
                    <ToggleButton value="ytd">{t('common.ytd')}</ToggleButton>
                  </ToggleButtonGroup>

                  <IconButton
                    size="small"
                    onClick={() => handleDateShift('forward')}
                    disabled={dateRange === 'ytd'}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Box>

                <DatePickerField
                  label={t('common.startDate')}
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                />

                <DatePickerField
                  label={t('common.endDate')}
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                />

                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  fullWidth
                  sx={{
                    mt: 1,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  {t('common.clearFilters')}
                </Button>
              </Stack>
            </Menu>

            <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, mb: 2 }}>
              <StatCard
                title={viewMode === 'agency' ? t('dashboard.agencies') : t('dashboard.agents')}
                value={viewMode === 'agency' ? stats?.uniqueAgencies || 0 : stats?.uniqueAgents || 0}
                icon={<GroupsIcon />}
                color="#2C9C6C"
              />
              <Paper
                sx={{
                  flex: 2,
                  minWidth: 0,
                  p: { xs: 1, sm: 1.5 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 0.8,
                      borderRadius: '10px',
                      bgcolor: 'rgba(44, 156, 108, 0.08)',
                      color: '#4DC896',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& svg': { fontSize: '1.1rem' },
                    }}
                  >
                    <LocalAtmIcon />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'text.secondary',
                    }}
                  >
                    {t('common.ap')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: '#F0F3F2',
                    borderRadius: '8px',
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography
                    component="p"
                    sx={{
                      fontSize: { xs: '1.3rem', sm: '1.5rem' },
                      fontWeight: 700,
                      color: 'text.primary',
                    }}
                  >
                    {formatCurrency(stats?.totalAP || 0)}
                  </Typography>
                </Box>
              </Paper>
              <StatCard
                title={t('dashboard.households')}
                value={stats?.totalCloses || 0}
                icon={<HomeWorkIcon />}
                color="#c9a84c"
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Leaderboard Section */}
        <Grid item xs={12}>
            <TopThreeDisplay writers={topWriters.slice(0, 3)} profilePictures={profilePictures} />

            <TableContainer
              component={Paper}
              sx={{
                mt: 1,
                boxShadow: 'none',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'transparent',
                overflowX: 'auto',
                '& .MuiTable-root': {
                  minWidth: 650,
                },
                '& .MuiTableCell-root': {
                  whiteSpace: 'nowrap',
                }
              }}
            >
              <Table
                aria-label="writer table"
                sx={{
                  '& .MuiTableCell-head': {
                    fontWeight: 600,
                    backgroundColor: 'rgba(44, 156, 108, 0.05)',
                  },
                  '& .MuiTableRow-root:hover': {
                    backgroundColor: 'rgba(44, 156, 108, 0.08)',
                  },
                  '& .MuiTableCell-root': {
                    padding: '8px 16px',
                  },
                  '& .MuiTableBody-root > .MuiTableRow-root:nth-of-type(1) > .MuiTableCell-root': {
                    color: '#FFD700',
                    fontWeight: 600,
                  },
                  '& .MuiTableBody-root > .MuiTableRow-root:nth-of-type(3) > .MuiTableCell-root': {
                    color: '#C0C0C0',
                    fontWeight: 600,
                  },
                  '& .MuiTableBody-root > .MuiTableRow-root:nth-of-type(5) > .MuiTableCell-root': {
                    color: '#CD7F32',
                    fontWeight: 600,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        {viewMode === 'agency' ? t('common.agency') : t('common.agent')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'totalAP'}
                        direction={orderBy === 'totalAP' ? order : 'asc'}
                        onClick={() => handleSort('totalAP')}
                      >
                        {t('common.premium')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'policies'}
                        direction={orderBy === 'policies' ? order : 'asc'}
                        onClick={() => handleSort('policies')}
                      >
                        {t('common.policies')}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={40} />
                      </TableCell>
                    </TableRow>
                  ) : displayedWriters.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        {t('dashboard.noDataForFilters')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    (leaderboardExpanded ? displayedWriters : displayedWriters.slice(0, 3)).map((writer) => (
                      <WriterTableRow
                        key={writer.id}
                        writer={writer}
                        closes={writer.closes}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {displayedWriters.length > 3 && (
              <Button
                size="small"
                onClick={() => setLeaderboardExpanded(!leaderboardExpanded)}
                endIcon={leaderboardExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                sx={{ mt: 1, width: '100%', color: 'text.secondary' }}
              >
                {leaderboardExpanded ? t('dashboard.showLess') : t('dashboard.showAll')}
              </Button>
            )}
        </Grid>

        <Grid item xs={12}>
          <NewsSection isAdmin={isAdmin} t={t} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 